<template>
    <div v-if="hasTrigger" class="context-menu-wrapper">
        <div @click.stop="toggle" class="context-menu-trigger" ref="triggerRef">
            <slot name="trigger" :isOpen="isOpen" :toggle="toggle"></slot>
        </div>
        <template v-if="!teleport">
            <transition name="context-menu">
                <div v-if="isOpen" class="context_menu" ref="menuRef">
                    <slot></slot>
                </div>
            </transition>
        </template>
        <teleport v-else to="body">
            <transition name="context-menu">
                <div v-if="isOpen" class="context_menu context_menu--teleported" :style="teleportStyle" ref="menuRef">
                    <slot></slot>
                </div>
            </transition>
        </teleport>
    </div>
    <transition name="context-menu">
        <div v-if="!hasTrigger && modelValue" class="context_menu" :class="menuClass" ref="menuRef">
            <slot></slot>
        </div>
    </transition>
</template>

<script>
// Global manager to ensure only one menu is open at a time
let activeMenu = null;

export default {
    name: 'ContextMenu',
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        closeOnClickOutside: {
            type: Boolean,
            default: true
        },
        menuClass: {
            type: String,
            default: ''
        },
        teleport: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            openedAt: 0,
            internalOpen: false,
            teleportPosition: { top: 0, right: 0 },
            teleportPlacement: 'bottom',
            teleportPositioned: false,
            teleportAnchor: null,
            teleportUpdateFrame: null,
            teleportListenersActive: false
        }
    },
    computed: {
        hasTrigger() {
            return !!this.$slots.trigger;
        },
        isOpen() {
            return this.hasTrigger ? this.internalOpen : this.modelValue;
        },
        teleportStyle() {
            return {
                position: 'fixed',
                top: this.teleportPosition.top + 'px',
                right: this.teleportPosition.right + 'px',
                zIndex: 1001,
                visibility: this.teleportPositioned ? 'visible' : 'hidden',
                '--context-menu-transform-origin': this.teleportPlacement === 'top' ? 'bottom right' : 'top right'
            }
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.onOpen();
            } else if (!newVal && oldVal) {
                this.onClose();
            }
        },
        internalOpen(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.onOpen();
            } else if (!newVal && oldVal) {
                this.onClose();
            }
        }
    },
    mounted() {
        if (this.modelValue || this.internalOpen) {
            this.onOpen();
        }
        if (this.closeOnClickOutside) {
            document.addEventListener('click', this.handleClickOutside);
        }
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        this.removeTeleportListeners();
        this.cancelTeleportPositionUpdate();
        if (activeMenu === this) {
            activeMenu = null;
        }
    },
    methods: {
        handleClickOutside(event) {
            if (!this.isOpen) return;

            // Ignore clicks that happen immediately after menu opened (same event loop)
            if (Date.now() - this.openedAt < 50) return;

            const menu = this.$refs.menuRef;
            if (menu && !menu.contains(event.target)) {
                this.close();
            }
        },
        async updateTeleportPosition() {
            if (this.teleportAnchor) {
                await this.updateTeleportPointPosition();
                return;
            }

            const trigger = this.$refs.triggerRef;
            if (!trigger) return;

            await this.$nextTick();
            if (!this.isOpen || !this.$refs.triggerRef) return;

            const rect = this.$refs.triggerRef.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) {
                this.close();
                return;
            }

            const menu = this.$refs.menuRef;
            const menuHeight = menu?.offsetHeight || 0;
            const viewportMargin = 8;
            const gap = 8;
            const spaceBelow = window.innerHeight - rect.bottom - gap - viewportMargin;
            const spaceAbove = rect.top - gap - viewportMargin;
            const shouldOpenUp = menuHeight > 0 && spaceBelow < menuHeight && spaceAbove > spaceBelow;
            let top = shouldOpenUp
                ? rect.top - gap - menuHeight
                : rect.bottom + gap;

            if (menuHeight > 0 && menuHeight <= window.innerHeight - viewportMargin * 2) {
                top = Math.min(
                    Math.max(top, viewportMargin),
                    window.innerHeight - viewportMargin - menuHeight
                );
            }

            this.teleportPlacement = shouldOpenUp ? 'top' : 'bottom';
            this.teleportPosition = {
                top: Math.round(top),
                right: Math.max(0, Math.round(window.innerWidth - rect.right))
            };
            this.teleportPositioned = true;
        },
        async updateTeleportPointPosition() {
            await this.$nextTick();
            if (!this.isOpen || !this.teleportAnchor) return;

            const menu = this.$refs.menuRef;
            const menuHeight = menu?.offsetHeight || 0;
            const menuWidth = menu?.offsetWidth || 0;
            const viewportMargin = 8;
            const gap = 0;
            const anchor = this.teleportAnchor;
            const spaceBelow = window.innerHeight - anchor.y - gap - viewportMargin;
            const spaceAbove = anchor.y - gap - viewportMargin;
            const shouldOpenUp = menuHeight > 0 && spaceBelow < menuHeight && spaceAbove > spaceBelow;
            let top = shouldOpenUp ? anchor.y - gap - menuHeight : anchor.y + gap;
            let left = anchor.x;

            if (menuHeight > 0 && menuHeight <= window.innerHeight - viewportMargin * 2) {
                top = Math.min(
                    Math.max(top, viewportMargin),
                    window.innerHeight - viewportMargin - menuHeight
                );
            }

            if (menuWidth > 0 && menuWidth <= window.innerWidth - viewportMargin * 2) {
                left = Math.min(
                    Math.max(anchor.x, viewportMargin),
                    window.innerWidth - viewportMargin - menuWidth
                );
            }

            this.teleportPlacement = shouldOpenUp ? 'top' : 'bottom';
            this.teleportPosition = {
                top: Math.round(top),
                right: Math.max(0, Math.round(window.innerWidth - left - menuWidth))
            };
            this.teleportPositioned = true;
        },
        queueTeleportPositionUpdate() {
            if (!this.isOpen || !this.teleport) return;
            if (this.teleportUpdateFrame) return;

            this.teleportUpdateFrame = window.requestAnimationFrame(() => {
                this.teleportUpdateFrame = null;
                this.updateTeleportPosition();
            });
        },
        cancelTeleportPositionUpdate() {
            if (!this.teleportUpdateFrame) return;
            window.cancelAnimationFrame(this.teleportUpdateFrame);
            this.teleportUpdateFrame = null;
        },
        addTeleportListeners() {
            if (this.teleportListenersActive || !this.teleport) return;
            window.addEventListener('scroll', this.queueTeleportPositionUpdate, true);
            window.addEventListener('resize', this.queueTeleportPositionUpdate);
            this.teleportListenersActive = true;
        },
        removeTeleportListeners() {
            if (!this.teleportListenersActive) return;
            window.removeEventListener('scroll', this.queueTeleportPositionUpdate, true);
            window.removeEventListener('resize', this.queueTeleportPositionUpdate);
            this.teleportListenersActive = false;
        },
        toggle() {
            if (this.hasTrigger) {
                this.teleportAnchor = null;
                if (!this.internalOpen && this.teleport) this.teleportPositioned = false;
                this.internalOpen = !this.internalOpen;
            } else {
                this.$emit('update:modelValue', !this.modelValue);
            }
        },
        openAt(x, y) {
            if (!this.hasTrigger || !this.teleport) return;

            this.teleportAnchor = { x, y };
            this.teleportPositioned = false;
            if (!this.internalOpen) {
                this.internalOpen = true;
            } else {
                this.onOpen();
            }
        },
        close() {
            if (this.hasTrigger) {
                this.internalOpen = false;
            } else {
                this.$emit('update:modelValue', false);
            }
        },
        onOpen() {
            // Close any other open menu
            if (activeMenu && activeMenu !== this) {
                activeMenu.close();
            }
            activeMenu = this;
            this.openedAt = Date.now();
            if (this.hasTrigger && this.teleport) {
                this.teleportPositioned = false;
                this.$nextTick(this.updateTeleportPosition);
                this.addTeleportListeners();
            }
        },
        onClose() {
            if (activeMenu === this) {
                activeMenu = null;
            }
            this.removeTeleportListeners();
            this.cancelTeleportPositionUpdate();
            this.teleportPositioned = false;
            this.teleportAnchor = null;
        }
    }
}
</script>

<style scoped>
.context-menu-wrapper {
    position: relative;
    display: inline-block;
}

.context-menu-trigger {
    cursor: pointer;
}

.context_menu {
    max-width: 440px;
    overflow: visible;
    background: rgba(47, 47, 47, 0.8);
    overscroll-behavior: contain;
    padding: 0.25rem;
    border: 1px solid rgba(47, 47, 47, 1);
    border-radius: 0.75rem;
    min-width: 13rem;
    backdrop-filter: blur(12.3px);
    -webkit-backdrop-filter: blur(12.3px);
    box-shadow: inset 0 2px 4px 0 #ffffff06;
    z-index: 1000;
}

.context-menu-wrapper .context_menu:not(.context_menu--teleported) {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
}

/* Animation */
.context-menu-enter-active,
.context-menu-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
    transform-origin: var(--context-menu-transform-origin, top right);
}

.context-menu-enter-from,
.context-menu-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
}

.context-menu-enter-to,
.context-menu-leave-from {
    opacity: 1;
    transform: scale(1) translateY(0);
}
</style>
